/**
 * Settings → Access Control: manage app-owned groups, module/tab grants, and data scopes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import InfoHint from '../common/InfoHint';
import accessControlApi, {
  AccessGroupDetail,
  AccessGroupSummary,
  PermissionCatalog,
} from '../../services/accessControlApi';
import { MODULE_PERMISSIONS, CANONICAL_DEVICE_TYPES } from '../../types/accessControl';
import apiService from '../../services/api';
import { useCourseLabels } from '../../hooks/useCourseLabels';

const emptyDetail = (): Omit<AccessGroupDetail, 'id'> & { id?: number } => ({
  name: '',
  description: '',
  is_active: true,
  permissions: [],
  members: [],
  data_scopes: [],
});

const AccessControlAdminPanel: React.FC = () => {
  const { courseSingular, coursePlural } = useCourseLabels();
  const [groups, setGroups] = useState<AccessGroupSummary[]>([]);
  const [catalog, setCatalog] = useState<PermissionCatalog | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ReturnType<typeof emptyDetail>>(emptyDetail());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [memberInput, setMemberInput] = useState('');
  const [courses, setCourses] = useState<string[]>([]);
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const modules = catalog?.modules?.length
    ? catalog.modules.map((m) => ({
        key: m.key,
        label: m.label,
        tabs: m.tabs || [],
      }))
    : MODULE_PERMISSIONS.map((m) => ({
        key: m.key,
        label: m.label,
        tabs: m.tabs,
      }));

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, cat] = await Promise.all([
        accessControlApi.listGroups(),
        accessControlApi.getCatalog().catch(() => null),
      ]);
      setGroups(list);
      if (cat) setCatalog(cat);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load access groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    (async () => {
      try {
        const courseData = await apiService.getCourses();
        if (Array.isArray(courseData)) {
          setCourses(
            courseData
              .map((c: any) => c.course_id || c.id || c.name)
              .filter(Boolean)
              .sort()
          );
        } else if (courseData && typeof courseData === 'object') {
          setCourses(Object.keys(courseData).sort());
        }
      } catch {
        /* ignore */
      }
      try {
        const fv = await apiService.client.get('/admin/device-references/field-values/entity_type');
        const types = (fv.data?.values || []).filter(Boolean).sort();
        if (types.length) setEntityTypes(types);
        else setEntityTypes(['Student', 'Trainee', 'RP', 'SRP', 'Operator', 'Manpack']);
      } catch {
        setEntityTypes(['Student', 'Trainee', 'RP', 'SRP', 'Operator', 'Manpack']);
      }
    })();
  }, []);

  const selectGroup = async (id: number) => {
    setError(null);
    setSuccess(null);
    setSelectedId(id);
    try {
      const detail = await accessControlApi.getGroup(id);
      setDraft({
        id: detail.id,
        name: detail.name,
        description: detail.description || '',
        is_active: detail.is_active,
        permissions: [...detail.permissions],
        members: detail.members.map((m) => ({
          user_key: m.user_key,
          display_name: m.display_name,
        })),
        data_scopes: detail.data_scopes.map((s) => ({
          mode: (s.mode === 'block' ? 'block' : 'allow') as 'allow' | 'block',
          entity_type: s.entity_type,
          device_type: s.device_type,
          course_id: s.course_id,
        })),
      });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load group');
    }
  };

  const startCreate = () => {
    setSelectedId(null);
    setDraft(emptyDetail());
    setSuccess(null);
    setError(null);
  };

  const permissionSet = useMemo(() => new Set(draft.permissions), [draft.permissions]);

  const togglePermission = (key: string) => {
    setDraft((d) => {
      const next = new Set(d.permissions);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...d, permissions: Array.from(next) };
    });
  };

  const toggleModule = (moduleKey: string, tabKeys: string[]) => {
    setDraft((d) => {
      const next = new Set(d.permissions);
      const hasModule = next.has(moduleKey);
      const hasAnyTab = tabKeys.some((t) => next.has(t));
      if (hasModule || hasAnyTab) {
        next.delete(moduleKey);
        tabKeys.forEach((t) => next.delete(t));
      } else {
        next.add(moduleKey);
      }
      return { ...d, permissions: Array.from(next) };
    });
  };

  const addMember = () => {
    const key = memberInput.trim().toLowerCase();
    if (!key) return;
    if (draft.members.some((m) => m.user_key === key)) {
      setMemberInput('');
      return;
    }
    setDraft((d) => ({
      ...d,
      members: [...d.members, { user_key: key }],
    }));
    setMemberInput('');
  };

  const removeMember = (userKey: string) => {
    setDraft((d) => ({
      ...d,
      members: d.members.filter((m) => m.user_key !== userKey),
    }));
  };

  const addScope = () => {
    setDraft((d) => ({
      ...d,
      data_scopes: [
        ...d.data_scopes,
        { mode: 'block', entity_type: null, device_type: null, course_id: null },
      ],
    }));
  };

  const updateScope = (
    index: number,
    patch: Partial<{
      mode: 'allow' | 'block';
      entity_type: string | null;
      device_type: string | null;
      course_id: string | null;
    }>
  ) => {
    setDraft((d) => {
      const scopes = [...d.data_scopes];
      scopes[index] = { ...scopes[index], ...patch };
      return { ...d, data_scopes: scopes };
    });
  };

  const removeScope = (index: number) => {
    setDraft((d) => ({
      ...d,
      data_scopes: d.data_scopes.filter((_, i) => i !== index),
    }));
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError('Group name is required');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        name: draft.name.trim(),
        description: draft.description || null,
        is_active: draft.is_active,
        permissions: draft.permissions,
        members: draft.members,
        data_scopes: draft.data_scopes.map((s) => ({
          mode: s.mode === 'block' ? 'block' : 'allow',
          entity_type: s.entity_type || null,
          device_type: s.device_type || null,
          course_id: s.course_id || null,
        })),
      };
      let saved: AccessGroupDetail;
      if (draft.id) {
        saved = await accessControlApi.updateGroup(draft.id, body);
      } else {
        saved = await accessControlApi.createGroup(body);
      }
      setSuccess(`Saved “${saved.name}”`);
      await loadGroups();
      await selectGroup(saved.id);
      // Reload session so COP/Pathfinder pick up new data scopes for this user
      try {
        const { useAuthStore } = await import('../../stores/authStore');
        await useAuthStore.getState().checkAuth();
      } catch {
        /* ignore */
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!draft.id) return;
    setSaving(true);
    try {
      await accessControlApi.deleteGroup(draft.id);
      setConfirmDelete(false);
      startCreate();
      await loadGroups();
      setSuccess('Group deleted');
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  const cardSx = {
    p: { xs: 2, md: 2.25 },
    borderRadius: 2.5,
    border: 1,
    borderColor: 'divider',
    bgcolor: (t: { palette: { mode: string } }) =>
      t.palette.mode === 'dark' ? 'rgba(255,255,255,0.022)' : 'background.paper',
  };

  return (
    <Box>
      <Stack direction="row" alignItems="flex-end" spacing={0.75} sx={{ mb: 2.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: -0.4, lineHeight: 1.15 }}>
            Access Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Groups, modules, and data scopes
          </Typography>
        </Box>
        <InfoHint title="How access groups work">
          <p>
            App-managed groups, independent of Entra. Assign users by email/UPN, grant modules and
            sub-tabs, and optionally restrict visible data by entity type, device type, and{' '}
            {courseSingular.toLowerCase()}.
          </p>
          <p>
            Users in no group keep the normal role-based experience. If you add yourself to a group,
            only that group’s module grants and data scopes apply (Settings stays available).
            Sign out/in or save the group to refresh your session.
          </p>
        </InfoHint>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Box sx={{ ...cardSx, width: { xs: '100%', md: 280 }, flexShrink: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Groups
            </Typography>
            <Button size="small" startIcon={<AddIcon />} onClick={startCreate}>
              New
            </Button>
          </Stack>
          <List dense sx={{ maxHeight: 480, overflow: 'auto', mx: -0.5 }}>
            {loading && (
              <ListItem>
                <ListItemText primary="Loading…" />
              </ListItem>
            )}
            {!loading && groups.length === 0 && (
              <ListItem>
                <ListItemText primary="No groups yet" secondary="Create one to get started" />
              </ListItem>
            )}
            {groups.map((g) => (
              <ListItemButton
                key={g.id}
                selected={selectedId === g.id}
                onClick={() => selectGroup(g.id)}
                sx={{ borderRadius: 1, mb: 0.25 }}
              >
                <ListItemText
                  primary={g.name}
                  secondary={`${g.member_count} members · ${g.permission_count} perms · ${g.data_scope_count} scopes`}
                />
                {!g.is_active && <Chip size="small" label="Off" />}
              </ListItemButton>
            ))}
          </List>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            <Box sx={cardSx}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Group name"
                  value={draft.name}
                  onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                  fullWidth
                  size="small"
                />
                <FormControlLabel
                  sx={{ flexShrink: 0, m: 0, minWidth: 120, justifyContent: 'space-between' }}
                  labelPlacement="start"
                  control={
                    <Switch
                      color="primary"
                      checked={draft.is_active}
                      onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
                    />
                  }
                  label="Active"
                />
              </Stack>
              <TextField
                label="Description"
                value={draft.description || ''}
                onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                fullWidth
                size="small"
                multiline
                minRows={2}
                sx={{ mt: 2 }}
              />
            </Box>

            <Box sx={cardSx}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Members
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                label="Email or UPN"
                value={memberInput}
                onChange={(e) => setMemberInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addMember();
                  }
                }}
              />
              <Button variant="outlined" onClick={addMember} startIcon={<GroupIcon />}>
                Add
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {draft.members.map((m) => (
                <Chip
                  key={m.user_key}
                  label={m.user_key}
                  onDelete={() => removeMember(m.user_key)}
                />
              ))}
              {draft.members.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No members — add emails/UPNs that match login identity.
                </Typography>
              )}
            </Stack>
            </Box>

            <Box sx={cardSx}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Module &amp; tab permissions
              </Typography>
              <InfoHint title="Module grants">
                Checking a module with no sub-tabs selected grants all of its tabs. Checking
                specific tabs grants only those.
              </InfoHint>
            </Stack>
            <Box sx={{ maxHeight: 380, overflow: 'auto' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 3,
                  rowGap: 0,
                }}
              >
              {modules.map((mod) => {
                const tabKeys = mod.tabs.map((t) => t.key);
                const moduleOn = permissionSet.has(mod.key);
                const anyTab = tabKeys.some((t) => permissionSet.has(t));
                return (
                  <Box
                    key={mod.key}
                    sx={{ py: 1.25, borderBottom: 1, borderColor: 'divider' }}
                  >
                    <FormControlLabel
                      sx={{ m: 0, width: '100%', justifyContent: 'space-between', ml: 0 }}
                      labelPlacement="start"
                      control={
                        <Checkbox
                          checked={moduleOn || anyTab}
                          indeterminate={!moduleOn && anyTab}
                          onChange={() => toggleModule(mod.key, tabKeys)}
                        />
                      }
                      label={<Typography fontWeight={650} sx={{ fontSize: '0.9rem' }}>{mod.label}</Typography>}
                    />
                    {mod.tabs.length > 0 && (
                      <Box sx={{ pl: 0.25, display: 'flex', flexDirection: 'column' }}>
                        {mod.tabs.map((tab) => (
                          <FormControlLabel
                            key={tab.key}
                            sx={{ m: 0, width: '100%', justifyContent: 'space-between' }}
                            labelPlacement="start"
                            control={
                              <Checkbox
                                size="small"
                                checked={permissionSet.has(tab.key) || (moduleOn && !anyTab)}
                                onChange={() => {
                                  // If only module was set, expand to all tabs then toggle this one off/on
                                  setDraft((d) => {
                                    const next = new Set(d.permissions);
                                    if (next.has(mod.key) && !tabKeys.some((t) => next.has(t))) {
                                      next.delete(mod.key);
                                      tabKeys.forEach((t) => next.add(t));
                                    }
                                    if (next.has(tab.key)) next.delete(tab.key);
                                    else next.add(tab.key);
                                    return { ...d, permissions: Array.from(next) };
                                  });
                                }}
                              />
                            }
                            label={<Typography variant="body2">{tab.label}</Typography>}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
              </Box>
            </Box>
            </Box>

            <Box sx={cardSx}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Data scopes
                </Typography>
                <InfoHint title="How scopes apply">
                  <p>
                    Per-row <strong>Allow</strong> or <strong>Block</strong>. Blank fields mean
                    “any”. Blocks always win.
                  </p>
                  <p>
                    If any Allow rows exist, a device must match an Allow and not match a Block.
                    Block-only = show everything except blocked.
                  </p>
                  <p>Example: Mode=Block, Entity=Student → hide all student devices.</p>
                </InfoHint>
              </Stack>
              <Button size="small" startIcon={<AddIcon />} onClick={addScope}>
                Add scope
              </Button>
            </Stack>
            <Stack spacing={1}>
              {draft.data_scopes.map((scope, idx) => (
                <Stack
                  key={idx}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ sm: 'center' }}
                >
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel>Mode</InputLabel>
                    <Select
                      label="Mode"
                      value={scope.mode === 'block' ? 'block' : 'allow'}
                      onChange={(e) =>
                        updateScope(idx, {
                          mode: e.target.value === 'block' ? 'block' : 'allow',
                        })
                      }
                    >
                      <MenuItem value="allow">Allow</MenuItem>
                      <MenuItem value="block">Block</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                    <InputLabel>Entity type</InputLabel>
                    <Select
                      label="Entity type"
                      value={scope.entity_type || ''}
                      onChange={(e) =>
                        updateScope(idx, { entity_type: e.target.value || null })
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      {entityTypes.map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                    <InputLabel>Device type</InputLabel>
                    <Select
                      label="Device type"
                      value={scope.device_type || ''}
                      onChange={(e) =>
                        updateScope(idx, { device_type: e.target.value || null })
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      {(catalog?.device_types || CANONICAL_DEVICE_TYPES).map((t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                    <InputLabel>{courseSingular}</InputLabel>
                    <Select
                      label={courseSingular}
                      value={scope.course_id || ''}
                      onChange={(e) =>
                        updateScope(idx, { course_id: e.target.value || null })
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      {courses.map((c) => (
                        <MenuItem key={c} value={c}>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton aria-label="Remove scope" onClick={() => removeScope(idx)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              ))}
              {draft.data_scopes.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No data scopes — members see all entity/device/{coursePlural.toLowerCase()} data
                  within granted modules.
                </Typography>
              )}
            </Stack>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {draft.id && (
                <Button color="error" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  Delete
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={save}
                disabled={saving}
              >
                {draft.id ? 'Save changes' : 'Create group'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete access group?</DialogTitle>
        <DialogContent>
          This permanently removes “{draft.name}” and its members, permissions, and scopes.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={doDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessControlAdminPanel;
